import React, { useRef, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, Route, Switch, useLocation } from "react-router-dom";
import { ScreenSearch, Button, Empty, TableWarningModal, Table2, Select, Breadcrumbs, ProductCreateDropdown } from "components";
import { getProductList, deleteProduct as deleteProductAction, duplicateProduct } from "./actions";
import { Tooltip, toastAlert } from "@paylixecommerce/shared/components";
import config from "constants/config";
import MobileProducts from "./mobile-products";
import roleHOC from "HOC/roleHOC";
import {
    SubscriptionProduct,
    LicenseProduct,
    SingleProduct,
    VariantsProduct,
    InfoCard,
    TableEdit,
    TableCopy,
    TableShare,
    TableDelete,
    DotsIcon
} from "../../../icons";
import { useOnClickOutside } from "components/hooks";

import CreateProductVariant from "./screens/create/variant";
import CreateProductInfo from "./screens/create/info";
import CreateProduct from "./screens/create/product";

import EditProductVariant from "./screens/detail/variant";
import EditProductInfo from "./screens/detail/info";
import EditProduct from "./screens/detail/product";

import MobilePlaceholder from "../upselling/mobile-placeholder";

import { random } from "../../comms/appeal";
import moment from "moment";
import useMobile from "../../../../../hooks/useMobile";
import getCDNLink from "../../../../../constants/cdn-link";
import { PureFileIcon } from "../../dashboard/sections/payments";

import "./index.scss";


const InfinityIcon = () => <svg
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    x="0px"
    y="0px"
    viewBox="0 0 60 60"
    xmlSpace="preserve"
    style={{ enableBackground: "new 0 0 60 60", width: 10, height: 10, fill: "var(--black9)" }}
>
<path d="M43.594,12.5c-9.046,0-16.406,7.851-16.406,17.5c0,6.341-4.836,11.5-10.781,11.5S5.625,36.341,5.625,30
	s4.836-11.5,10.781-11.5c2.146,0,4.218,0.67,5.992,1.938c0.593,0.425,1.147,0.911,1.645,1.445c1.061,1.136,2.914,1.14,3.977,0.009
	c1.099-1.167,1.103-3.07,0.009-4.243c-0.76-0.813-1.601-1.552-2.499-2.194c-2.704-1.933-5.858-2.954-9.124-2.954
	C7.36,12.5,0,20.351,0,30s7.36,17.5,16.406,17.5S32.812,39.649,32.812,30c0-6.341,4.836-11.5,10.781-11.5S54.375,23.659,54.375,30
	s-4.836,11.5-10.781,11.5c-2.879,0-5.586-1.195-7.622-3.366c-1.062-1.133-2.915-1.133-3.978,0c-0.531,0.567-0.823,1.32-0.823,2.121
	c0,0.802,0.293,1.556,0.824,2.121c3.098,3.305,7.218,5.124,11.599,5.124C52.64,47.5,60,39.649,60,30S52.64,12.5,43.594,12.5z"/>
</svg>;


export const search = (data, filters, searchKey) =>
    data.filter(product => {
        for (let i = 0; i < filters.length; i++) {
            let prod = product[filters[i]];
            if (
                (prod || (prod === 0 && filters[i] === 'stock')) &&
                prod.toString().toLowerCase().includes(searchKey.toLowerCase())
            ) {
                return true;
            }
        }
        return false;
    });

export const Actions = ({ data, showConfirm, isAdmin }) => {

    let shop = useSelector(({auth}) => (auth.shop ? auth.shop.name : ''));
    let dispatch = useDispatch();
    let isInfo = data.type === "INFO_CARD";
    let isSerials = data.type === "SERIALS";
    let isVariant = data.price_variants;

    let duplicateAction = uniqid => {
        dispatch(duplicateProduct({uniqid}))
            .then(res => {
                toastAlert('success', res.message);
                dispatch(getProductList());
            })
            .catch(err => {
                toastAlert('error', err ? err.error : '');
            });
    };

    return (
        <div className="paylixecommerce-table-actions">
            <DotsIcon />

            <div className="paylixecommerce-table-actions-modal">
                <Link to={isInfo ? `${isAdmin ? isAdmin : ''}/products/edit/info/${data.uniqid}` : isVariant ? `${isAdmin ? isAdmin : ''}/products/edit/variant/${data.uniqid}` : `${isAdmin ? isAdmin : ''}/products/edit/${data.uniqid}`}>
                    <TableEdit/>
                    <span>Edit</span>
                </Link>
                {isSerials ? <Link to={{
                    pathname: `${isAdmin ? isAdmin : ''}/products/edit/${data.uniqid}`,
                    state: { isSerials }
                }}>
                    <TableEdit/>
                    <span>Serials</span>
                </Link> : null}
                <div onClick={() => duplicateAction(data.uniqid)}>
                    <TableCopy/>
                    <span>Duplicate</span>
                </div>
                <Tooltip copiedText={`https://${shop}.paylix.gg/product/${data.uniqid}`} successText="Link copied" tooltipText="Copy shared link" onCopy={() => toastAlert("success", "Product link copied to clipboard")}>
                    <>
                        <TableShare/>
                        <span>Share</span>
                    </>
                </Tooltip>
                <div onClick={() => showConfirm(data)} className="delete-icon">
                    <TableDelete/>
                    <span>Delete</span>
                </div>
            </div>
        </div>
    );
};

const sortFunc = (a1, b1, id) => {
    let aStock = 0;
    let bStock = 0;

    if (a1.original[id] === -1) {
        aStock = 99999999999999;
    }

    if (a1.original[id] !== -1) {
        aStock = Number(a1.original[id]);
    }

    if (b1.original[id] === -1) {
        bStock = 99999999999999;
    }

    if (b1.original[id] !== -1) {
        bStock = Number(b1.original[id]);
    }

    if (aStock > bStock) {
        return 1;
    }
    if (bStock > aStock) {
        return -1;
    }

    return 0;
};

const sortFuncPrice = (a1, b1, id) => {
    const a = Number(a1.original[id]);
    const b = Number(b1.original[id]);

    if (a > b) return 1;

    if (b > a) return -1;

    return 0;
};

const Product = ({ products, isAdmin }) => {

    const [pageIndex, setPageIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [searchKey, setSearchKey] = useState('');
    const [searchType, setSearchType] = useState('');
    const [searchStatus, setSearchStatus] = useState('');
    const [searchStock, setSearchStock] = useState('');
    const [confirm, showConfirm] = useState(null);

    const { pathname } = useLocation();
    const isMobile = useMobile();
    const dispatch = useDispatch();

    products = isAdmin ? products || [] : useSelector(({ products }) => products.product_list);

    useEffect(() => {
        dispatch(getProductList())
            .catch(err => toastAlert('error', (err && err.error) || 'Something went wrong!'))
            .finally(() => setLoading(false));
    }, [pathname]);

    let deleteProduct = uniqid => {
        setLoading(true);

        dispatch(deleteProductAction({uniqid}))
            .then(res => {
                dispatch(getProductList());
                toastAlert('success', res.message);
            })
            .catch(err => toastAlert('error', (err && err.error) || 'Something went wrong!'))
            .finally(() => {
                setLoading(false);
                showConfirm(null);
            });
    };


    let filterByType = products => {
        if (searchType === 'all') {
            return products;
        } else {
            if(searchType === "LICENSE") {
                return products.filter(({ type, licensing_enabled }) => (type === "SERIALS" && licensing_enabled));
            } else {
                return products.filter(({ type }) => (type === searchType));
            }

        }
    };

    let PRODUCT_TYPES = [{
        label: "All Types",
        value: "all"
    }, {
        label: "Serials",
        value: "SERIALS"
    }, {
        label: "File",
        value: "FILE",
    }, {
        label: "Service",
        value: "SERVICE",
    }, {
        label: "Dynamic",
        value: "DYNAMIC",
    }, {
        label: "Info Card",
        value: "INFO_CARD",
    }, {
        label: "Subscription",
        value: "SUBSCRIPTION",
    }, {
        label: "License",
        value: "LICENSE",
    }]

    let PRODUCT_STOCK = [{
        label: "Product Stock",
        value: "all",
    }, {
        label: "Available",
        value: "in",
    }, {
        label: "Out of Stock",
        value: "out",
    }, {
        label: "Last",
        value: "last",
    }];

    let PRODUCT_STATUS = [{
        label: "All Statuses",
        value: "all"
    }, {
        label: "Private",
        value: "private"
    }, {
        label: "On Hold",
        value: "on_hold",
    }, {
        label: "Unlisted",
        value: "unlisted",
    }, {
        label: "Live",
        value: "live",
    }]

    let filterByStatus = products => {
        if (searchStatus === 'all') {
            return products;
        } else {
            return products.filter((product) => {
                if(product.private && searchStatus === "private") {
                    return product;
                }
                if(product.on_hold && searchStatus === "on_hold") {
                    return product;
                }
                if(product.unlisted && searchStatus === "unlisted") {
                    return product;
                }
                if(!(product.private || product.on_hold || product.unlisted) && product.linked && searchStatus === "live") {
                    return product;
                }
            });
        }
    };

    let filterByStock = products => {
        if (searchStock === 'all') {
            return products;
        } else {
            return products.filter((product) => {
                if (product.type === "INFO_CARD") {
                    return false;
                }

                if ((product.stock === -1 || product.stock > 1) && searchStock === 'in') {
                    return product;
                }

                if (product.stock === 0 && searchStock === 'out') {
                    return product;
                }

                if (product.stock === 1 && searchStock === 'last') {
                    return product;
                }
            });
        }
    };

    let data = products;
    if(searchKey) data = search(products, ['title', 'type', 'stock', 'revenue'], searchKey);
    if (searchType) data = filterByType(data);
    if (searchStatus) data = filterByStatus(data);
    if (searchStock) data = filterByStock(data);

    let isEmpty = !loading && !products.length;

    const columns = [{
            Header: 'Product',
            accessor: 'title',
            Cell: ({ row }) => {

                const isInfo = row.original.type === "INFO_CARD";
                const isVariant = row.original.price_variants;
                let link = isInfo ? `/products/edit/info/${row.original.uniqid}` : isVariant ? `/products/edit/variant/${row.original.uniqid}` : `/products/edit/${row.original.uniqid}`;
                if(isAdmin) {
                    link = isAdmin + link;
                }

                return <Link to={link} className="product-table-type">
                    <div className="product-table-type-icon" >{row.original.cloudflare_image_id ? <img src={getCDNLink({ id: row.original.cloudflare_image_id })} alt=""/> : <PureFileIcon />}</div>
                    <div className="product-table-type-title">
                        <div>{row.original.title}</div>
                        <div className="product-table-type-date">{moment(row.original.created_at * 1000).format('DD, MMM YYYY HH:mm')}</div>
                    </div>
                </Link>
            }
        }, {
            Header: 'ID',
            accessor: 'uniqid'
        }, {
            Header: 'Type',
            accessor: 'type',
            Cell: ({ row, value }) => {
                if(value === "SERIALS" && row.original.licensing_enabled) {
                    return <div className="badge badge-paylixecommerce">LICENSE</div>
                } else {
                    return <div className="badge badge-paylixecommerce">{value.replace("_", " ")}</div>
                }
            }
        },  {
            Header: 'Status',
            accessor: 'linked',
            Cell: ({ row }) =>  <div>
                {row.original.private ? <div className="badge badge-bad">Private</div> : null}
                {row.original.on_hold ? <div className="badge badge-bad">On Hold</div> : null}
                {row.original.unlisted ? <div className="badge badge-bad">Unlisted</div> : null}
                {row.original.private || row.original.unlisted || row.original.on_hold ? null : row.original.linked ? <div className="badge badge-good" style={{ width: "fit-content" }}>Live</div> : <div className="badge badge-bad" style={{ width: "fit-content" }}>Deleted</div>}
            </div>
        }, {
            Header: 'Stock',
            accessor: 'stock',
            Cell: ({row, value}) => {
                if (row.original.type === "INFO_CARD") {
                    value = "-";
                }
                if (value === -1) {
                    value = <span style={{fontSize: 16}}><InfinityIcon /></span>
                }
                return value
            },
            sortType: sortFunc
        }, {
            Header: 'Price',
            accessor: 'price_display',
            Cell: ({row, value}) => row.original.type === "INFO_CARD" || +row.original.price_display === 0 ? "Free" : <>{config.CURRENCY_LIST[row.original.currency]}{value}</>,
            sortType: sortFuncPrice
        }, {
            Header: '',
            accessor: 'options',
            disableSortBy: true,
            Cell: ({ row }) => <Actions isAdmin={isAdmin} showConfirm={() => showConfirm(row.original)} data={row.original} />
        }
    ];

    const placeholder = [{
            Header: 'Product',
            accessor: 'title',
            Cell: () => (
                <Link to="/" className="product-table-type">
                    <div className="product-table-type-icon" ><PureFileIcon /></div>
                    <div className="product-table-type-title">
                        <div><div className="skeleton skeleton2 medium" style={{ width: random(120, 250) }} /></div>
                        <div className="product-table-type-date"><div className="skeleton skeleton2 small" style={{ width: random(120, 120) }} /></div>
                    </div>
                </Link>
            )
        }, {
            Header: 'ID',
            accessor: 'uniqid',
            Cell: () => <div className="skeleton skeleton4 medium" style={{ width: random(120) }} />
        }, {
            Header: 'Product Type',
            accessor: 'type',
            Cell: () => <div className="badge badge-paylixecommerce">Paylix</div>
        }, {
            Header: 'Stock',
            accessor: 'stock',
            Cell: () => <div className="skeleton skeleton5" style={{ width: random(30, 80) }} />
        },  {
            Header: 'Status',
            accessor: 'linked',
            Cell: () => <div className="badge badge-paylixecommerce">Paylix</div>
        }, {
            Header: 'Price',
            accessor: 'price_display',
            Cell: () => <div className="skeleton skeleton1" style={{ width: random(30, 80) }} />
        }, {
            Header: '',
            accessor: 'options',
            disableSortBy: true,
            Cell: ({ row }) => <Actions showConfirm={() => showConfirm(row.original)} data={row.original} />
        }
    ];

    return (
        <div className="product-screen">

            <TableWarningModal
                close={() => showConfirm(null)}
                open={confirm}
                deleteAction={deleteProduct}
                type="product"
                isLoading={loading}
            />

            {isEmpty ? null :
                <div className="screen-header">
                    {isAdmin ? null : <Breadcrumbs title="Products" breadcrumbs={[{ title: 'Products', link: '/' }, { title: 'Products', link: '/products' }]} />}

                    <div className="screen-filters">
                        <div className="product-screen-filters-block">
                            <Select
                                size="large"
                                value={searchType}
                                onChange={e => setSearchType(e.target.value)}
                                options={PRODUCT_TYPES.map(({ label, value }) => ({ label, value }))}
                            />
                            <Select
                                size="large"
                                value={searchStatus}
                                onChange={e => setSearchStatus(e.target.value)}
                                options={PRODUCT_STATUS.map(({ label, value }) => ({ label, value }))}
                            />
                            <Select
                                size="large"
                                value={searchStock}
                                onChange={e => setSearchStock(e.target.value)}
                                options={PRODUCT_STOCK.map(({ label, value }) => ({ label, value }))}
                            />
                        </div>
                        <ScreenSearch onChange={setSearchKey} search={searchKey} />

                        {isAdmin ? null :
                            <ProductCreateDropdown 
                                buttonText="Create"
                                className="product-menu"
                            />
                        }


                    </div>
                </div>
            }

            {isEmpty ? <Empty isProducts/> : null}

            {
                isMobile ?
                    null :
                    loading ?
                        <Table2 pageIndex={pageIndex} data={[1,2,3,4,5,6,7,8,9,10]} pageSize={10} isPlaceholder columns={placeholder} className="last-right" /> :
                        isEmpty ?
                            null :
                            <Table2
                                setPageIndex={setPageIndex}
                                pageIndex={pageIndex}
                                pageSize={10}
                                data={data}
                                columns={columns}
                                className="last-right"
                                clearFilters={() => {
                                    setSearchStock("all");
                                    setSearchStatus("all");
                                    setSearchType("all");
                                    setSearchKey("");
                                }}
                            />
            }
            {
                isMobile ?
                    loading ?
                        <MobilePlaceholder data={[1, 2, 3]} /> :
                        isEmpty ?
                            null :
                        <MobileProducts
                            data={data}
                            showConfirm={showConfirm}
                            clearFilters={() => {
                                setSearchStock("all");
                                setSearchStatus("all");
                                setSearchType("all");
                                setSearchKey("");
                            }}
                        /> :
                    null
            }

            <Switch>
                <Route exact={true} path={`${isAdmin ? isAdmin : ''}/products/new/info`}>
                    <>
                        <div className="edit-modal-overlay" />
                        <div className="edit-modal padding">
                            <CreateProductInfo pageIndex={pageIndex} />
                        </div>
                        <style dangerouslySetInnerHTML={{__html: `html { overflow: hidden !important }`}}/>
                    </>
                </Route>
                <Route exact={true} path={`${isAdmin ? isAdmin : ''}/products/new/variant`}>
                    <>
                        <div className="edit-modal-overlay" />
                        <div className="edit-modal padding">
                            <CreateProductVariant pageIndex={pageIndex} />
                        </div>
                        <style dangerouslySetInnerHTML={{__html: `html { overflow: hidden !important }`}}/>
                    </>
                </Route>
                <Route exact={true} path={`${isAdmin ? isAdmin : ''}/products/new`}>
                    <>
                        <div className="edit-modal-overlay" />
                        <div className="edit-modal padding">
                            <CreateProduct pageIndex={pageIndex} />
                        </div>
                        <style dangerouslySetInnerHTML={{ __html: `html { overflow: hidden !important }` }}/>
                    </>
                </Route>
                <Route exact={true} path={`${isAdmin ? isAdmin : ''}/products/edit/info/:id`}>
                    <>
                        <div className="edit-modal-overlay" />
                        <div className="edit-modal padding">
                            <EditProductInfo isAdmin={isAdmin} pageIndex={pageIndex} />
                        </div>
                    </>
                </Route>
                <Route exact={true} path={`${isAdmin ? isAdmin : ''}/products/edit/variant/:id`}>
                    <>
                        <div className="edit-modal-overlay" />
                        <div className="edit-modal padding">
                            <EditProductVariant isAdmin={isAdmin} pageIndex={pageIndex} />
                        </div>
                    </>
                </Route>
                <Route exact={true} path={`${isAdmin ? isAdmin : ''}/products/edit/:id`}>
                    <>
                        <div className="edit-modal-overlay" />
                        <div className="edit-modal padding">
                            <EditProduct isAdmin={isAdmin} pageIndex={pageIndex} />
                        </div>
                    </>
                </Route>
            </Switch>
        </div>
    );
};

export default roleHOC(Product);
